<template>
  <div>
    <h2 class="mb-3" v-if="id">{{ title }}</h2>

    <b-card class="mb-3" body-class="px-3 py-3" v-if="id">
      <h5 class="mb-2"><b>Only show customers in either of these segments:</b></h5>
      <b-row>
        <b-col :md="9">
          <segment-picker v-model="segments" class="mb-md-0 mb-2" />
        </b-col>
        <b-col :md="3" align-self="center">
          <b-button :disabled="hasNoSegmentChange || isSavingSegments"
                    @click="updateDefaultSegments"
                    variant="success"
                    size="lg"
                    block >
            <i class="fa fa-spin fa-spinner mr-2" v-if="isSavingSegments" ></i>
            Set as default
          </b-button>
        </b-col>
      </b-row>
      <div class="text-success pl-1">
        <small>
          <strong>
            <i>{{ items.length }} customers</i>
          </strong>
        </small>
      </div>
    </b-card>

    <b-row class="mb-1">
      <b-col :md="6" :sm="8">
        <b-form-group class="mt-2 mb-0">
          <b-input-group size="lg" class="mb-0">
            <b-form-input v-model="filter" placeholder="Type to Search" />
            <b-input-group-append>
              <b-button :disabled="!filter" @click="filter = ''" style="z-index: 0;">Clear</b-button>
            </b-input-group-append>
          </b-input-group>
        </b-form-group>
      </b-col>
      <b-col :sm="4" :md="6" class="text-right" align-self="end">
        <b-button variant="outline-primary" size="lg" class="px-5" @click="startPrecisionMarketing" v-if="id">
          <i class="fa fa-envelope mr-2"></i>
          <b>Send Messages</b>
        </b-button>
        <h2 v-else>{{ title }}</h2>
      </b-col>
    </b-row>

    <b-card>
      <b-table
        show-empty
        stacked="md"
        :items="items"
        :fields="fields"
        :current-page="currentPage"
        :per-page="perPage"
        :filter="filter"
        @filtered="onFiltered">
        <template slot="show_details" slot-scope="row">
          <b-button size="sm" @click.stop="showDetails(row.index)" class="mr-2">
            Show Details
          </b-button>
        </template>
      </b-table>

      <b-pagination :total-rows="totalRows" :per-page="perPage" v-model="currentPage" class="mb-0" />
    </b-card>

    <precision-marketing
      v-if="precisionMarketingSessionID"
      :key="precisionMarketingSessionID"
      :name="title"
      @close="precisionMarketingSessionID = null" />
  </div>
</template>

<script>
  import _ from 'lodash';
  import PrecisionMarketing from '../PrecisionMarketing';
  import SegmentPicker from './SegmentPicker';
  import {defaultListSegments, defaultListColumns} from '../../utils';
  import Rules from '../../rules';

  export default {
    name: 'LView',

    components: {
      SegmentPicker,
      PrecisionMarketing
    },

    data() {
      return {
        title: null,

        fields: [],
        db: [],
        filter: null,

        currentPage: 1,
        perPage: 8,
        totalRows: 0,

        originalSegments: [],
        segments: [],
        isSavingSegments: false,

        precisionMarketingSessionID: null
      };
    },

    computed: {
      id() {
        return this.$route.params.id || null;
      },

      currentList() {
        return this.$store.state.lists.db.find(({id}) => id === this.id);
      },

      hasNoSegmentChange() {
        return _.isEqual(_.sortBy(this.originalSegments), _.sortBy(this.segments));
      },

      items() {
        const segments = _.filter(this.$store.state.segments, ({id}) => _.includes(this.segments, id));
        let db = _.cloneDeep(this.db);

        if (segments.length) {
          const OperatorCheckMap = _.mapValues(Rules, rules => _.mapValues(_.keyBy(rules, 'key'), 'check'));

          db = _.filter(db, (row) => {
            return _.some(segments, ({conditions}) => {
              return _.every(conditions, ({column, operator, value, value2}) => {
                const type = this.columnType(column);
                return type ? OperatorCheckMap[type][operator](row[column], value, value2) : true;
              });
            });
          });
        }

        return _.map(db, (row, i) => {
          row.SFL_RANK = i + 1;
          return row;
        });
      }
    },

    created() {
      let {currentList} = this;
      if (!this.id) {
        currentList = {
          name: 'Customer 360',
          filters: [],
          defaultSegments: [],
          selectedColumns: defaultListColumns()
        };
      }

      const db = this.$store.state.database;
      const dbKeys = currentList.selectedColumns;

      const sortFields = _.map(currentList.filters, ({column}) => column);
      const sortDirections = _.map(currentList.filters, ({ascending}) => (ascending ? 'asc' : 'desc'));

      const tempFields = _.map(dbKeys, key => ({key, sortable: _.indexOf(sortFields, key) > -1}));
      tempFields.push({key: 'show_details'}); // add the extra show details to go into 360
      tempFields.unshift({key: 'SFL_RANK', label: '#', class: 'font-weight-bold'});

      this.title = currentList.name;

      const s = defaultListSegments(currentList.defaultSegments);
      this.segments = _.cloneDeep(s);
      this.originalSegments = _.cloneDeep(s);

      this.totalRows = db.length;
      this.fields = tempFields;
      this.db = _.orderBy(db, sortFields, sortDirections);
    },

    methods: {
      columnType(name) {
        const column = _.find(this.$store.getters.getCustomerFileColumns, {name});
        return column ? column.type : null;
      },

      onFiltered(filteredItems) {
        // Trigger pagination to update the number of buttons/pages due to filtering
        this.totalRows = filteredItems.length;
        this.currentPage = 1;
      },

      showDetails(index) {
        const customerID = this.items[index][this.fields[0].key]; // assumes the first field in db is primary key
        this.$router.push(`/view/${customerID}`);
      },

      startPrecisionMarketing() {
        this.precisionMarketingSessionID = (new Date()).getMilliseconds();
      },

      updateDefaultSegments() {
        const {currentList} = this;
        currentList.defaultSegments = this.segments;
        this.$store.commit('SaveList', currentList);
        this.originalSegments = _.cloneDeep(this.segments);
      }
    }
  };
</script>

<style scoped>

</style>
